import { useState } from "react";
import { toast } from "sonner";
import { MessageSquarePlus, Star } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { api, formatApiError } from "@/lib/api";

const CATEGORIES = ["general", "matches", "onboarding", "verification", "deal_room", "usability", "bug"];

export function BetaFeedbackButton() {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button data-testid="beta-feedback-open" onClick={() => setOpen(true)}
        className="h-9 px-3 rounded-md border border-slate-200 text-sm text-slate-600 flex items-center gap-1.5 hover:bg-slate-50">
        <MessageSquarePlus size={15} /> <span className="hidden sm:inline">Feedback</span>
      </button>
      <BetaFeedbackDialog open={open} onOpenChange={setOpen} />
    </>
  );
}

export function BetaFeedbackDialog({ open, onOpenChange, context = "general" }) {
  const [rating, setRating] = useState(0);
  const [category, setCategory] = useState("general");
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (busy) return;
    if (!rating) { toast.error("Please pick a rating (1–5)."); return; }
    setBusy(true);
    try {
      await api.post("/beta/feedback", { rating, category, comment: comment.trim(), context });
      toast.success("Thanks for your feedback!");
      onOpenChange(false); setRating(0); setCategory("general"); setComment("");
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || "Could not submit feedback."); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!busy) onOpenChange(o); }}>
      <DialogContent className="max-w-md" data-testid="beta-feedback-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><MessageSquarePlus size={18} className="text-[#2563EB]" /> Share beta feedback</DialogTitle>
          <DialogDescription>How is your experience so far? Your rating helps us improve the beta.</DialogDescription>
        </DialogHeader>
        <div className="grid gap-3 py-1">
          <div className="grid gap-1.5">
            <Label>Rating</Label>
            <div className="flex gap-1" data-testid="beta-feedback-rating">
              {[1, 2, 3, 4, 5].map((n) => (
                <button key={n} type="button" data-testid={`beta-rating-${n}`} onClick={() => setRating(n)}
                  className="p-1" aria-label={`${n} star`}>
                  <Star size={26} className={n <= rating ? "fill-amber-400 text-amber-400" : "text-slate-300"} />
                </button>
              ))}
            </div>
          </div>
          <div className="grid gap-1.5">
            <Label>Category</Label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger data-testid="beta-feedback-category"><SelectValue /></SelectTrigger>
              <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="fb-comment">Comment (optional)</Label>
            <Textarea id="fb-comment" data-testid="beta-feedback-comment" rows={3} value={comment} onChange={(e) => setComment(e.target.value)} placeholder="What worked well? What was confusing?" />
          </div>
        </div>
        <DialogFooter>
          <button data-testid="beta-feedback-cancel" disabled={busy} onClick={() => onOpenChange(false)} className="h-9 px-3 rounded-md border border-slate-200 text-sm disabled:opacity-40">Cancel</button>
          <button data-testid="beta-feedback-submit" disabled={busy || !rating} onClick={submit} className="h-9 px-4 rounded-md bg-[#2563EB] text-white text-sm disabled:opacity-40">{busy ? "Sending…" : "Submit"}</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
